// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { executePolling, REQUEST_STATUS_COMPLETED } from '../../../../../share/util/api/polling';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { OBD_READINESS_STATUS } from '../../../../../constants/usageCountTypes';
import { createObdReadinessStatusItems } from '../../../../../util/obd/diag';
import { pushFunctionList } from '../../../../../router/obd';
import apiService from '../../../../../share/util/api/service';
import alert from '../../../../../share/alert';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import diagnoseResource from '../../../../../share/util/diagnose';
import loading from '../../../../../share/components/vue/ProcessingIcon';
import util from '../../../../../share/util/utils';
import memoPage from '../../../../vue/MemoPage';
import eventLogUtil from '../../../../../share/util/eventLogUtil';
import config from '../../../../../share/config';
import { OBD_READINESS_STATUS_LIMIT } from '../../../../../util/obd/functions';
import { getMeasuredDataName, getCsvFileName } from '../../../../../util/fileExport';

export default {
  name: 'readinessStatus',
  data() {
    return {
      measureId: undefined,
      processing: false,
      getReadinessStatusResponse: undefined,
      readinessStatusResultItems: [],
      readinessStatusResultColumns: [],
      isMemoDialogLoading: false,
      modalMemo: false,
      isExecutedFromStartScreen: false,
    };
  },

  props: {
    obdFunctionLabelKey: { type: String, required: false, default: 'fixed_resource.obd_menu' },
    functionNameKey: { type: String, required: false, default: 'fixed_resource.obd_menu_readiness_status' },
    destinationAPI: { type: String, required: false, default: apiService.OBD },
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'memo-page': memoPage,
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      netAppId: state => state.netAppConnector.pairingAppId,
      projectId: state => state.vehicle.vehicleInfo ? state.vehicle.vehicleInfo.vehicle.project.id : null,
      obdDataId: state => state.obd.obdDataId,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        if (!this.getReadinessStatusResponse) {
          return;
        }
        this.updateTable();
        this.updateColumns();
      }
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      // データ読出し
      this.execute();
      // パスに"menu"を含まない場合はStart画面からの実行
      if (!this.$route.path.includes('menu')) {
        this.isExecutedFromStartScreen = true;
      }
    },

    /** レディネスステータス読み出し */
    async execute() {
      try {
        this.processing = true;
        const systemNo = this.$route.params.systemNo;
        // イベントログの操作 ID 発行
        await eventLogUtil.createEventLogHandleId(this.netAppId);
        const response = await executePolling(
          this,
          // レディネスステータス情報取得結果(要求)(POST API) 呼び出しのコールバック
          async () => {
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${systemNo}/readiness`,
            );
          },
          // レディネスステータス情報取得状況の監視(GET API) 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // OBDシステムの利用回数カウントアップ
        this.usageCountUp({ type: OBD_READINESS_STATUS });

        // responseが存在しない場合、処理終了
        if (!response) return;

        if (response.data.status === REQUEST_STATUS_COMPLETED) {
          // ポーリングが正常に完了した場合は、一覧表示を行う
          this.measureId = response.data.results.measure_id;
          this.getReadinessStatusResponse = await this.getReadinessStatusResult();
          // responseが存在しない場合、処理終了
          if (!this.getReadinessStatusResponse) return;

          this.updateTable();
          this.updateColumns();
        } else {
          // 異常終了 : 実行不可画面
          const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
          alert.showError(this, null,
            messages,
            () => {
              // 遷移処理
              pushFunctionList(this, this.$route.params.systemNo);
            });
        }
      } finally {
        this.processing = false;
      }
    },

    // レディネスステータス情報取得結果
    async getReadinessStatusResult() {
      let results = null;
      let response = null;
      const params = { last_evaluated_key: null };
      do {
        response = await executeSyncRequest(
          this,
          async () => {
            return await apiService.getParamCall(
              apiService.OBD,
              `/obd/${this.obdDataId}/measures/${this.measureId}/readiness`,
              params,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          results = results ? results.concat(response.data.data.results) : response.data.data.results;
          if (results.length >= OBD_READINESS_STATUS_LIMIT) break;
          params.last_evaluated_key = response.data.data.last_evaluated_key ? JSON.stringify(response.data.data.last_evaluated_key) : null;
        }
      } while (params.last_evaluated_key);
      // 処理失敗時は response が null になるので考慮
      if (results) {
        if (results.length >= OBD_READINESS_STATUS_LIMIT) {
          return results.slice(0, OBD_READINESS_STATUS_LIMIT);
        }
        else {
          return results;
        }
      }
      else {
        return null;
      }
    },

    /**
     * テーブルヘッダーを作り直す
     */
    updateColumns() {
      if (!this.getReadinessStatusResponse) return;

      this.readinessStatusResultColumns = [
        { name: 'readinessGroup', label: 'fixed_resource.header_readiness_readiness_group', useResource: true },
        { name: 'status', label: 'fixed_resource.header_vehicle_dtc_table_status' },
        { name: 'dtc', label: 'fixed_resource.header_vehicle_dtc_table_code', originalSortFunc: diagnoseResource.sortFuncCode },
        { name: 'description', label: 'fixed_resource.header_vehicle_dtc_table_description', useResource: true, originalSortFunc: this.sortFuncDescription },
      ];
    },

    /**
     * テーブル情報を更新する
     */
    async updateTable() {
      this.readinessStatusResultItems = createObdReadinessStatusItems(this.getReadinessStatusResponse, this);
    },

    /**
     * メモダイアログを表示する
     */
    openNoteModal() {
      this.modalMemo = true;
    },

    /**
     * メモダイアログを非表示にする
     */
    closeNoteModal() {
      this.modalMemo = false;
    },

    /**
     * メモ情報の保存
     */
    async memoSave() {
      await this.$refs.memo.save();
    },

    /**
     * メモ情報のクリア
     */
    memoClear: function () {
      this.$refs.memo.clear();
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t('fixed_resource.obd_function_common_message_result_ng_2');
      alert.showError(this, null, messages,
        () => {
          // 遷移処理
          pushFunctionList(this, this.$route.params.systemNo);
        });
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    // レディネスステータス情報をCSVエクスポートする
    async csvExport() {
      // 行のフォーマット
      const resultInfo = this.readinessStatusResultItems.map(item => {
        return {
          ...item,
          readinessGroup: this.$t(item.readinessGroup),
          description: this.$t(item.description),
        };
      });
      // VINデータのフォーマット
      // Start画面から遷移した場合は、VIN取得をしないため、空文字で出力する。
      const vinData = this.isExecutedFromStartScreen ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '');
      const vinHeader = `"${this.$t('message.label_measured_data_search_vin')}","${vinData}"\n`;
      // 出力形式の設定
      const headers = this.readinessStatusResultColumns.map((column) => {
        return { id: column.name, title: this.$t(column.label) };
      });
      // 説明・故障部位のエスケープ処理
      const record = util.escapeDoubleQuotation(resultInfo, 'description');

      let endPoint = '';
      let measuredDataName = '';

      if (!this.isExecutedFromStartScreen && this.projectId !== null && this.measureId !== undefined) {
        try {
          this.processing = true;
          // メモボタン活性（パスに"menu"が存在）かつプロジェクトIDおよび計測データIDが存在する場合、計測データ取得APIを指定
          endPoint = '/projects/' + this.projectId + '/' + this.measureId;
          // 計測データ名を取得
          measuredDataName = await getMeasuredDataName(this, this.destinationAPI, endPoint, this.operationScreen);
        } finally {
          this.processing = false;
        }
      }
      const fileName = getCsvFileName(
        `${this.$t(this.functionNameKey)}${this.$t(this.obdFunctionLabelKey)}`,
        measuredDataName,
        this.isExecutedFromStartScreen ? '' : (this.vehicleInfo?.vehicle?.vin?.vin || '')
      );

      // CSVファイルエクスポート処理
      const data = vinHeader + util.createCsvData(headers, record);
      util.csvFileExport(data, fileName);
    },

    /**
     * 再取得ボタン押下時の処理を行う
     */
    async reGetReadinessStatus() {
      const messages = this.$t('individual.message_diag_code_confirm_get_information');
      alert.showConfirmation(this, null,
        messages,
        () => {
          // レディネスステータスの取得
          this.execute();
        });
    },

    // 説明・故障部位独自ソート
    sortFuncDescription(param) {
      if (!param.data) {
        return null;
      }
      // 結果リスト
      let resultItems = [];
      // 指定外コードリスト
      const unspecifiedList = [];
      // DTC割り当て有りリスト
      let dtcDescriptionLists = [];
      param.data.forEach(element => {
        if (element.description.indexOf('message.status_vehicle_dtc_description_unspecified') > -1) {
          unspecifiedList.push(element);
        }
        // dtcの説明かどうかを判定
        if (element.description.indexOf('dtc.') > -1) {
          dtcDescriptionLists.push(element);
        }
      });
      dtcDescriptionLists = this.sortItems(dtcDescriptionLists, 'description', param.sortDir);

      // 昇順の場合
      if (param.sortDir === config.ASC) {
        resultItems = dtcDescriptionLists.concat(unspecifiedList);
        return resultItems;
      }
      // 降順の場合
      else {
        resultItems = unspecifiedList.concat(dtcDescriptionLists);
        return resultItems;
      }
    },

    // 配列のソート処理 
    sortItems(items, sortKey, sortDir = config.DESC) {
      let resultItems = items;
      if (items) {
        resultItems = items
          .sort((a, b) => {
            if (this.$t(a[sortKey]) !== this.$t(a[sortKey]) && this.$t(b[sortKey]) !== this.$t(b[sortKey])) return 0;
            if (this.$t(a[sortKey]) !== this.$t(a[sortKey])) return 1;
            if (this.$t(b[sortKey]) !== this.$t(b[sortKey])) return -1;

            if (this.$t(a[sortKey]) === null && this.$t(b[sortKey]) === null) return 0;
            if (this.$t(a[sortKey]) === null) return 1;
            if (this.$t(b[sortKey]) === null) return -1;

            if (this.$t(a[sortKey]) === '' && this.$t(b[sortKey]) === '') return 0;
            if (this.$t(a[sortKey]) === '') return 1;
            if (this.$t(b[sortKey]) === '') return -1;

            const sig = sortDir === config.DESC ? 1 : -1;
            return this.$t(a[sortKey]) < this.$t(b[sortKey]) ? sig : (this.$t(a[sortKey]) > this.$t(b[sortKey]) ? -sig : 0);
          });
      }
      return resultItems;
    },
  },

  mounted() {
    this.mountedLogic();
  },
};
